import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import {
	DndContext,
	PointerSensor,
	closestCenter,
	useSensor,
	useSensors,
} from '@dnd-kit/core'
import {
	SortableContext,
	arrayMove,
	useSortable,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import { SET_RERENDER } from 'store/rerender/actions'
import { httpOk, GET, POST, PUT } from 'helpers/api'

function SortableFavorite({ item, onRemove }) {
	const { t } = useTranslation()
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id: item.id })

	return (
		<li
			ref={setNodeRef}
			className={`fav-row ${isDragging ? 'fav-row-dragging' : ''}`}
			style={{ transform: CSS.Transform.toString(transform), transition }}>
			<button type="button" className="fav-drag-handle" {...attributes} {...listeners}>
				<i className="uil uil-draggabledots"></i>
			</button>
			<span className="fav-icon">
				<i className={item.icon || 'uil-circle'}></i>
			</span>
			<div className="fav-row-body">
				<div className="fav-row-name">{t(item.name)}</div>
				{item.url && <div className="fav-row-url">{item.url}</div>}
			</div>
			<button type="button" className="fav-remove" onClick={() => onRemove(item)}>
				<i className="uil uil-times"></i>
			</button>
		</li>
	)
}

function Index() {
	const { t } = useTranslation()
	const dispatch = useDispatch()
	const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))

	const [menu, setMenu] = useState([])
	const [favorites, setFavorites] = useState([])
	const [activeSection, setActiveSection] = useState(null)
	const [search, setSearch] = useState('')

	// Первый элемент ответа — служебная секция «Избранное», остальное — реальные разделы
	const sections = useMemo(() => menu.slice(1), [menu])

	const filteredSections = useMemo(() => {
		const query = search.trim().toLowerCase()
		if (!query) return sections

		return sections
			.map(section => {
				const children = (section.children || []).filter(item =>
					!item.title && String(t(item.name) || item.name).toLowerCase().includes(query)
				)
				if (!children.length) return null
				return { ...section, children }
			})
			.filter(Boolean)
	}, [sections, search, t])

	async function searchWithFilter() {
		const response = await GET('/users/navigation', {}, { loader: true })
		if (httpOk(response)) {
			const data = response.data.data
			setMenu(data)
			setFavorites((data?.[0]?.children || []).filter(item => item.id))
		}
	}

	function setActiveTab(item) {
		setActiveSection(activeSection === item.name ? null : item.name)
	}

	async function toggleFavorite(item) {
		const response = await POST('/users/favorite-navigation', { 'id': item.id }, { notification: false })
		if (!httpOk(response)) return

		const isFavorite = response.data.data

		setMenu(prev => prev.map(section => ({
			...section,
			children: (section.children || []).map(child => (
				child.id === item.id ? { ...child, is_favorite: isFavorite } : child
			)),
		})))

		setFavorites(prev => (
			isFavorite
				? [...prev, { ...item, is_favorite: true }]
				: prev.filter(favorite => favorite.id !== item.id)
		))

		dispatch(SET_RERENDER())
	}

	async function persistReorder(items) {
		await PUT('/users/favorite-navigation/reorder', {
			items: items.map((item, index) => ({
				id: item.id,
				order_number: index + 1,
			})),
		}, { notification: false })

		dispatch(SET_RERENDER())
	}

	function handleDragEnd(event) {
		const { active, over } = event
		if (!active || !over || active.id === over.id) return

		const oldIndex = favorites.findIndex(item => item.id === active.id)
		const newIndex = favorites.findIndex(item => item.id === over.id)
		if (oldIndex === -1 || newIndex === -1) return

		const reordered = arrayMove(favorites, oldIndex, newIndex)
		setFavorites(reordered)
		persistReorder(reordered)
	}

	useEffect(() => {
		searchWithFilter()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between align-items-center">
				<div>
					<h4 className="vertical-center mb-1">{t('favorites')}</h4>
					<p className="text-muted mb-0 fz-14">{t('favorites_page_hint')}</p>
				</div>
			</div>

			<div className="row g-3">
				<div className="col-lg-7 col-xl-8">
					<div className="card fav-card">
						<div className="card-body">
							<div className="fav-card-header">
								<h5 className="mb-0">{t('favorites_all_sections')}</h5>
							</div>

							<div className="fav-search">
								<i className="uil uil-search"></i>
								<input
									type="text"
									className="form-control"
									placeholder={t('search')}
									value={search}
									onChange={(e) => setSearch(e.target.value)}
								/>
								{!!search &&
									<button type="button" className="fav-search-clear" onClick={() => setSearch('')}>
										<i className="uil uil-times"></i>
									</button>
								}
							</div>

							{filteredSections.length === 0 ?
								<div className="fav-empty">
									<i className="uil uil-search"></i>
									<div>{t('not_found')}</div>
								</div>
								:
								<div className="fav-sections">
									{filteredSections.map((itemMenu, indexMenu) => {
										const expanded = Boolean(search.trim()) || activeSection === itemMenu.name
										const pages = (itemMenu.children || []).filter(item => !item.title)
										const favoriteCount = pages.filter(item => item.is_favorite).length

										return (
											<div className={`fav-section ${expanded ? 'fav-section-expanded' : ''}`} key={indexMenu}>
												<div className="fav-section-header" onClick={() => setActiveTab(itemMenu)}>
													<span className="fav-icon">
														<i className={itemMenu.icon || 'uil-apps'}></i>
													</span>
													<span className="fav-section-name">{t(itemMenu.name)}</span>
													{favoriteCount > 0 &&
														<span className="fav-badge fav-badge-sm">{favoriteCount}</span>
													}
													<i className={`arrow-icon uil-angle-${expanded ? 'up' : 'down'}`}></i>
												</div>

												{expanded &&
													<div className="fav-section-body">
														<div className="fav-section-list">
															{(itemMenu.children || []).map((item, index) => (
																item.title ?
																	<div className="fav-group-title" key={index}>{t(item.name)}</div>
																	:
																	<button
																		type="button"
																		key={index}
																		className={`fav-page ${item.is_favorite ? 'fav-page-active' : ''}`}
																		onClick={() => toggleFavorite(item)}>
																		<span className="fav-icon">
																			<i className={item.icon || 'uil-circle'}></i>
																		</span>
																		<span className="fav-page-name">{t(item.name)}</span>
																		<i className={`fav-star ${item.is_favorite ? 'uil-favorite' : 'uil-star'}`}></i>
																	</button>
															))}
														</div>
													</div>
												}
											</div>
										)
									})}
								</div>
							}
						</div>
					</div>
				</div>

				<div className="col-lg-5 col-xl-4">
					<div className="card fav-card">
						<div className="card-body">
							<div className="fav-card-header">
								<div>
									<h5 className="mb-0">{t('favorites_my')}</h5>
									<span className="text-muted fav-card-hint">{t('favorites_drag_hint')}</span>
								</div>
								<span className="fav-badge">{favorites.length}</span>
							</div>

							{favorites.length === 0 ?
								<div className="fav-empty">
									<i className="uil uil-star"></i>
									<div>{t('favorites_empty')}</div>
								</div>
								:
								<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
									<SortableContext items={favorites.map(item => item.id)} strategy={verticalListSortingStrategy}>
										<ul className="fav-list">
											{favorites.map(item => (
												<SortableFavorite key={item.id} item={item} onRemove={toggleFavorite} />
											))}
										</ul>
									</SortableContext>
								</DndContext>
							}
						</div>
					</div>
				</div>
			</div>

			<style>{`
				.fav-card { height: 100%; }
				.fav-card .card-body { padding: 12px; }
				.fav-card-header {
					display: flex;
					align-items: center;
					justify-content: space-between;
					gap: 10px;
					margin-bottom: 10px;
				}
				.fav-card-header h5 { font-size: 15px; }
				.fav-card-hint { font-size: 12px; line-height: 1.25; }
				.fav-badge {
					background: var(--dark-surface-3, #eef2f7);
					color: var(--dark-text-muted, #6c757d);
					border-radius: 20px;
					padding: 1px 9px;
					font-size: 12px;
					font-weight: 600;
					flex-shrink: 0;
				}
				.fav-badge-sm { font-size: 11px; padding: 1px 7px; }
				.fav-icon {
					width: 24px;
					height: 24px;
					border-radius: 6px;
					background: var(--dark-surface-2, #f1f4f9);
					color: var(--dark-text-muted, #5b6b7f);
					display: flex;
					align-items: center;
					justify-content: center;
					font-size: 14px;
					flex-shrink: 0;
				}
				.fav-list { list-style: none; padding: 0; margin: 0; }
				.fav-row {
					display: flex;
					align-items: center;
					gap: 8px;
					padding: 4px 8px;
					border: 1px solid var(--dark-border, #eef2f7);
					border-radius: 8px;
					background: var(--dark-surface, #fff);
					margin-bottom: 4px;
					transition: border-color .15s, box-shadow .15s;
				}
				.fav-row:hover { border-color: var(--dark-tint-border-primary, #d7e0ea); box-shadow: 0 2px 6px rgba(15, 34, 58, .06); }
				.fav-row-dragging { opacity: .85; position: relative; z-index: 2; box-shadow: 0 6px 16px rgba(15, 34, 58, .12); }
				.fav-row-body { flex-grow: 1; min-width: 0; }
				.fav-row-name { font-size: 13px; line-height: 1.2; }
				.fav-row-url {
					font-size: 10px;
					line-height: 1.2;
					color: var(--dark-text-muted, #98a6ad);
					overflow: hidden;
					text-overflow: ellipsis;
					white-space: nowrap;
				}
				.fav-drag-handle {
					border: 0;
					background: transparent;
					color: var(--dark-text-muted, #98a6ad);
					padding: 0;
					line-height: 1;
					cursor: grab;
					flex-shrink: 0;
				}
				.fav-drag-handle:active { cursor: grabbing; }
				.fav-remove {
					border: 0;
					background: transparent;
					color: #c2cdd6;
					padding: 0 2px;
					line-height: 1;
					flex-shrink: 0;
				}
				.fav-remove:hover { color: #f1556c; }
				.fav-empty {
					text-align: center;
					color: var(--dark-text-muted, #98a6ad);
					padding: 24px 8px;
					font-size: 13px;
				}
				.fav-empty i { font-size: 26px; display: block; margin-bottom: 6px; }
				.fav-search { position: relative; margin-bottom: 10px; }
				.fav-search i.uil-search {
					position: absolute;
					left: 10px;
					top: 50%;
					transform: translateY(-50%);
					color: var(--dark-text-muted, #98a6ad);
				}
				.fav-search .form-control {
					padding: 5px 30px 5px 32px;
					font-size: 13px;
				}
				.fav-search-clear {
					position: absolute;
					right: 8px;
					top: 50%;
					transform: translateY(-50%);
					border: 0;
					background: transparent;
					color: var(--dark-text-muted, #98a6ad);
					line-height: 1;
				}
				.fav-sections { display: flex; flex-direction: column; gap: 4px; }
				.fav-section { border: 1px solid var(--dark-border, #e7ecf3); border-radius: 8px; overflow: hidden; }
				.fav-section-expanded { border-color: var(--dark-tint-border-primary, #d7e0ea); }
				.fav-section-header {
					display: flex;
					align-items: center;
					gap: 8px;
					padding: 7px 10px;
					cursor: pointer;
					background: var(--dark-surface-2, #f4f7fa);
				}
				.fav-section-header:hover { background: var(--dark-surface-3, #eef2f7); }
				.fav-section-expanded .fav-section-header { border-bottom: 1px solid var(--dark-border, #e7ecf3); }
				.fav-section-name {
					flex-grow: 1;
					font-weight: 600;
					font-size: 13px;
					color: var(--dark-text, #3b4759);
				}
				/* Дети — на белом с отступом и направляющей линией слева */
				.fav-section-body { background: var(--dark-surface, #fff); padding: 4px 4px 4px 0; }
				.fav-section-list {
					border-left: 1px solid var(--dark-border, #e7ecf3);
					margin-left: 18px;
					padding-left: 6px;
				}
				.fav-group-title {
					font-size: 10px;
					text-transform: uppercase;
					letter-spacing: .04em;
					color: var(--dark-text-muted, #a6b0bd);
					padding: 6px 8px 2px;
				}
				.fav-page {
					display: flex;
					align-items: center;
					gap: 8px;
					width: 100%;
					border: 0;
					background: transparent;
					border-radius: 6px;
					padding: 4px 8px;
					text-align: left;
					transition: background .15s;
				}
				.fav-page:hover { background: var(--dark-surface-2, #f4f7fa); }
				.fav-page-name { flex-grow: 1; font-size: 13px; color: var(--dark-text-muted, #5b6b7f); }
				.fav-page .fav-icon { background: transparent; color: var(--dark-text-muted, #a6b0bd); width: 18px; height: 18px; font-size: 13px; }
				.fav-page .fav-star { color: #d3dae2; font-size: 15px; }
				.fav-page:hover .fav-star { color: #b6c1cd; }
				.fav-page-active .fav-page-name { color: var(--dark-text, #3b4759); }
				.fav-page-active .fav-icon { color: var(--dark-text-muted, #5b6b7f); }
				.fav-page-active .fav-star,
				.fav-page-active:hover .fav-star { color: #f7b84b; }
			`}</style>
		</>
	)
}

export default Index
